//import { JSDOM } from 'jsdom';

export enum Marketplace {
    Tmall = 'Tmall',
    Taobao = 'Taobao',
    Alibaba = '1688',
    TmallMobile = 'Tmall Mobile',
    TaobaoMobile = 'Taobao Mobile',
    AlibabaMobile = '1688 Mobile',
}
export interface MarketplaceInfo {
    marketplace: string
    productID?: string
    originalUrl?: string
    miId?: string
}

export function extractValidUrl(text: string): string | null {
    const urlRegex = /(https?:\/\/[^\s]+)/ // Regex tìm URL
    const match = text.match(urlRegex)
    return match ? match[0] : null // Lấy URL đầu tiên tìm thấy
}

function detectMarketplace(url: string): string {
    try {
        const { hostname, searchParams } = new URL(url)

        // Nếu link có miId thì là Sourcing (Taobao)
        if (
            searchParams.get('miId') ||
            searchParams.get('mi_id') ||
            searchParams.get('miid') ||
            /(?:[?&#]|%26|%3F)(?:mi_id|miId|miid)(?:=|%3D)/i.test(url)
        ) {
            return Marketplace.Taobao
        }

        if (hostname === 'qr.1688.com') return Marketplace.AlibabaMobile
        if (hostname.endsWith('.1688.com') || hostname === '1688.com') return Marketplace.Alibaba

        const marketplaces = [
            { name: Marketplace.Tmall, patterns: ['tmall.com', 'tmall.hk'] },
            { name: Marketplace.Taobao, patterns: ['taobao.com', 'taobao.global'] },
            { name: Marketplace.TmallMobile, patterns: ['m.tb.cn'] },
            { name: Marketplace.TaobaoMobile, patterns: ['e.tb.cn', 'tb.cn'] },
        ]

        const market = marketplaces.find(m =>
            m.patterns.some(pattern => hostname.endsWith(pattern) || hostname === pattern)
        )

        return market ? market.name : 'Unknown'
    } catch (e) {
        return 'Unknown'
    }
}

function extractProductId(url: string): string | undefined {
    try {
        const { hostname, searchParams, pathname } = new URL(url)

        if (hostname.includes('tmall.com') || hostname.includes('taobao.com')) {
            return searchParams.get('id') || searchParams.get('itemId') || undefined
        }

        if (hostname.includes('1688.com')) {
            const match = pathname.match(/\/offer\/(\d+)\.html/)
            return match ? match[1] : searchParams.get('offerId') || searchParams.get('id') || undefined
        }

        return undefined
    } catch (e) {
        return undefined
    }
}

export interface SourcingItemParams {
    miId?: string
    itemId?: string
    itemUrl?: string
    originalUrl?: string
}

export function extractSourcingParams(inputText: string): SourcingItemParams | null {
    if (!inputText || !inputText.trim()) {
        return null
    }

    const trimmedText = inputText.trim()

    // Trường hợp người dùng nhập trực tiếp ID thuần (chỉ chứa số)
    if (/^\d+$/.test(trimmedText)) {
        return { itemId: trimmedText }
    }

    const url = extractValidUrl(trimmedText)
    if (!url) {
        return null
    }

    try {
        const parsedUrl = new URL(url)
        const { searchParams, pathname } = parsedUrl

        // 1. Ưu tiên 1: Kiểm tra miId trong query params hoặc URL
        const miIdParam =
            searchParams.get('miId') ||
            searchParams.get('mi_id') ||
            searchParams.get('miid')
        if (miIdParam) {
            return { miId: miIdParam, originalUrl: url }
        }

        // Regex tìm miId dự phòng (kể cả trong fragment hoặc tham số mã hoá)
        const miIdRegexMatch =
            url.match(/(?:[?&#]|%26|%3F)(?:mi_id|miId|miid)(?:=|%3D)([^&#\s%]+)/i) ||
            url.match(/[?&](?:miId|mi_id|miid)=([^&#\s]+)/i)
        if (miIdRegexMatch && miIdRegexMatch[1]) {
            return { miId: decodeURIComponent(miIdRegexMatch[1]), originalUrl: url }
        }

        // 2. Ưu tiên 2: Trích xuất itemId
        // 1688 /offer/{id}.html
        const offerMatch = pathname.match(/\/offer\/(\d+)\.html/)
        if (offerMatch && offerMatch[1]) {
            return { itemId: offerMatch[1], originalUrl: url }
        }

        // Taobao, Tmall, 1688 query params: id, itemId, offerId, itemIds
        const idParam =
            searchParams.get('id') ||
            searchParams.get('itemId') ||
            searchParams.get('offerId') ||
            searchParams.get('itemIds')
        if (idParam) {
            return { itemId: idParam, originalUrl: url }
        }

        // 3. Ưu tiên 3: Nếu không bóc tách được miId và itemId (link rút gọn, mobile...), dùng itemUrl
        return { itemUrl: url, originalUrl: url }
    } catch (e) {
        // Fallback nếu không parse được qua URL object
        const miIdRegexMatch =
            url.match(/(?:[?&#]|%26|%3F)(?:mi_id|miId|miid)(?:=|%3D)([^&#\s%]+)/i) ||
            url.match(/[?&](?:miId|mi_id|miid)=([^&#\s]+)/i)
        if (miIdRegexMatch && miIdRegexMatch[1]) {
            return { miId: decodeURIComponent(miIdRegexMatch[1]), originalUrl: url }
        }
        return { itemUrl: url, originalUrl: url }
    }
}

export function parseMarketplaceUrl(inputText: string): MarketplaceInfo | null {
    const url = extractValidUrl(inputText)

    if (!url) {
        return null
    }

    try {
        const parsedUrl = new URL(url)
        const miIdMatch =
            parsedUrl.searchParams.get('miId') ||
            parsedUrl.searchParams.get('mi_id') ||
            parsedUrl.searchParams.get('miid') ||
            url.match(/(?:[?&#]|%26|%3F)(?:mi_id|miId|miid)(?:=|%3D)([^&#\s%]+)/i)?.[1] ||
            url.match(/[?&](?:miId|mi_id|miid)=([^&#\s]+)/i)?.[1]

        const miId = miIdMatch ? decodeURIComponent(miIdMatch) : undefined

        const marketplace = detectMarketplace(url)
        if (marketplace.includes('Mobile')) {
            return {
                marketplace,
                originalUrl: url,
                miId,
            }
        }

        return {
            marketplace,
            productID: extractProductId(url),
            originalUrl: url,
            miId,
        }
    } catch (error) {
        const miIdMatch =
            url.match(/(?:[?&#]|%26|%3F)(?:mi_id|miId|miid)(?:=|%3D)([^&#\s%]+)/i)?.[1] ||
            url.match(/[?&](?:miId|mi_id|miid)=([^&#\s]+)/i)?.[1]
        return {
            marketplace: 'Unknown',
            originalUrl: url,
            miId: miIdMatch ? decodeURIComponent(miIdMatch) : undefined,
        }
    }
}

//   const testText = "【淘宝】7天无理由退货 https://e.tb.cn/h.TrlY0SrtLfcglvN?tk=htQrei5eCd4 HU293";
//   console.log(parseMarketplaceUrl(testText));

// const { JSDOM } = require('jsdom');

// Ví dụ sử dụng
// const url = 'https://e.tb.cn/h.TrlY0SrtLfcglvN?tk=htQrei5eCd4';
// extractIdFromScript(url).then(console.log);
// console.log('------------------');
// const url2 = 'https://qr.1688.com/s/DO8Gw0F3';
// extractIdFromScript(url2).then(console.log);
